import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";
import { PageHead, card, Field, Sel, inputCls, useCatalog } from "@/components/admin/kit";

const D0 = { name: "", name_voice: "", specialty: "" };

export function useSaver(path, reload) {
  const api = useApi();
  return async (body, id) => {
    try {
      id ? await api.put(`${path}/${id}`, body) : await api.post(path, body);
      toast.success("Saved");
      reload();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
}

function HospitalForm({ init, onSave }) {
  const [f, setF] = useState(init);
  const s = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v }));
  return (
    <form className="grid gap-3 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); onSave({ ...f, price_per_patient: f.price_per_patient === "" || f.price_per_patient == null ? 69 : Number(f.price_per_patient) }); }} data-testid="hospital-form">
      <Field label="Name *"><input className={inputCls} required value={f.name} onChange={s("name")} data-testid="hospital-name-input" /></Field>
      <Field label="Name as spoken in the call" hint="e.g. శ్రీ సాయి హాస్పిటల్"><input className={`${inputCls} font-te`} value={f.name_voice} onChange={s("name_voice")} data-testid="hospital-voice-input" /></Field>
      <Field label="Type"><Sel value={f.type} onChange={s("type")} options={[["hospital", "Hospital"], ["clinic", "Clinic"], ["dental", "Dental clinic"], ["pharmacy", "Pharmacy"]]} testid="hospital-type-select" /></Field>
      <Field label="Default language"><input className={inputCls} value={f.default_language} onChange={s("default_language")} data-testid="hospital-lang-input" /></Field>
      <Field label="Town"><input className={inputCls} value={f.town} onChange={s("town")} data-testid="hospital-town-input" /></Field>
      <Field label="State"><input className={inputCls} value={f.state} onChange={s("state")} data-testid="hospital-state-input" /></Field>
      <Field label="Plan"><input className={inputCls} value={f.plan} onChange={s("plan")} data-testid="hospital-plan-input" /></Field>
      {f.type === "pharmacy" && <label className="flex items-center gap-2 self-end pb-2 text-sm font-semibold"><input type="checkbox" checked={!!f.offers_delivery} onChange={s("offers_delivery")} data-testid="hospital-delivery-checkbox" /> Offers home delivery (adds the delivery line to refill calls)</label>}
      <Field label="Short code"><input className={inputCls} maxLength={8} value={f.short_code || ""} onChange={s("short_code")} data-testid="hospital-code-input" /></Field>
      <Field label="Price per patient (₹ / month)"><input type="number" min={0} className={inputCls} value={f.price_per_patient ?? 69} onChange={s("price_per_patient")} data-testid="hospital-price-input" /></Field>
      <Field label="Status"><Sel value={f.status || "trial"} onChange={s("status")} options={[["trial", "Trial"], ["active", "Active"], ["suspended", "Suspended"]]} testid="hospital-status-select" /></Field>
      <Field label="Trial ends"><input type="date" className={inputCls} value={f.trial_end || ""} onChange={s("trial_end")} data-testid="hospital-trial-input" /></Field>
      <Button className="sm:col-span-2" data-testid="hospital-save-btn">Save organisation</Button>
    </form>
  );
}

export default function Hospitals() {
  const api = useApi();
  const { hospitals, doctors, reload } = useCatalog();
  const [edit, setEdit] = useState(null);
  const [doc, setDoc] = useState(null);
  const saveH = useSaver("/hospitals", reload);
  const saveD = useSaver("/doctors", reload);
  const nav = useNavigate();
  const setStatus = async (h, status) => {
    if (status === "suspended" && !window.confirm(`Suspend ${h.name}? Its reminder calls stop until you activate it.`)) return;
    try { await api.post(`/hospitals/${h.id}/status`, { status }); reload(); toast.success(status === "suspended" ? "Suspended — calls stopped" : "Activated — calls restart"); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (path, label) => {
    if (!window.confirm(`Delete ${label}? This also removes linked records.`)) return;
    try { await api.delete(path); reload(); toast.success("Deleted"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="hospitals-page">
      <PageHead title="Organisations" sub="Hospitals, clinics, dental clinics and pharmacies. View as opens their portal read-only.">
        <Button onClick={() => nav("/admin/onboarding")} data-testid="add-hospital-btn"><Plus size={16} /> Add organisation</Button>
      </PageHead>
      <div className="grid gap-4 lg:grid-cols-2">
        {hospitals.map((h) => (
          <div key={h.id} className={card} data-testid={`hospital-card-${h.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-bold">{h.name}</h3>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${h.status === "suspended" ? "bg-red-100 text-red-700" : h.status === "active" ? "bg-green-100 text-green-800" : "bg-sky-100 text-sky-800"}`} data-testid={`hospital-status-${h.id}`}>{h.status === "suspended" ? "Suspended" : h.status === "active" ? "Active" : "Trial"}{h.status !== "suspended" && h.status !== "active" && h.trial_end ? ` until ${h.trial_end}` : ""}</span>
              {h.short_code && <span className="text-xs font-bold text-muted-foreground">{h.short_code}</span>}
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="outline" onClick={() => nav(`/admin/view-as/${h.id}`)} data-testid={`view-as-${h.id}`}><Eye size={14} /> View as</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(h, h.status === "suspended" ? "active" : "suspended")} data-testid={`suspend-toggle-${h.id}`}>{h.status === "suspended" ? "Activate" : "Suspend"}</Button>
                <Button size="icon" variant="ghost" onClick={() => setEdit(h)} data-testid={`edit-hospital-${h.id}`}><Pencil size={15} /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(`/hospitals/${h.id}`, h.name)} data-testid={`delete-hospital-${h.id}`}><Trash2 size={15} /></Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">{h.type} · {h.town}, {h.state} · {h.default_language} · {h.plan}</p>
            {h.name_voice && <p className="font-te text-sm">{h.name_voice}</p>}
            <div className="mt-4 border-t border-[var(--lav-2)] pt-3">
              <div className="flex items-center"><p className="text-sm font-bold">Doctors</p>
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => setDoc({ ...D0, hospital_id: h.id })} data-testid={`add-doctor-${h.id}`}><Plus size={14} /> Doctor</Button></div>
              {doctors.filter((d) => d.hospital_id === h.id).map((d) => (
                <div key={d.id} className="flex items-center gap-2 py-1.5 text-sm">
                  <span className="font-medium">{d.name}</span><span className="font-te text-muted-foreground">{d.name_voice}</span><span className="text-xs text-muted-foreground">{d.specialty}</span>
                  <button className="ml-auto text-muted-foreground" onClick={() => setDoc(d)} data-testid={`edit-doctor-${d.id}`}><Pencil size={14} /></button>
                  <button className="text-muted-foreground" onClick={() => del(`/doctors/${d.id}`, d.name)} data-testid={`delete-doctor-${d.id}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"><DialogHeader><DialogTitle>Edit organisation</DialogTitle></DialogHeader>
          {edit && <HospitalForm init={edit} onSave={async (b) => (await saveH(b, edit.id)) && setEdit(null)} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!doc} onOpenChange={(o) => !o && setDoc(null)}>
        <DialogContent><DialogHeader><DialogTitle>{doc?.id ? "Edit doctor" : "Add doctor"}</DialogTitle></DialogHeader>
          {doc && (
            <form className="grid gap-3" onSubmit={async (e) => { e.preventDefault(); (await saveD(doc, doc.id)) && setDoc(null); }} data-testid="doctor-form">
              <Field label="Name *"><input className={inputCls} required value={doc.name} onChange={(e) => setDoc({ ...doc, name: e.target.value })} data-testid="doctor-name-input" /></Field>
              <Field label="Name as spoken" hint="e.g. రమేష్ — the call says డాక్టర్ {name} గారు"><input className={`${inputCls} font-te`} value={doc.name_voice} onChange={(e) => setDoc({ ...doc, name_voice: e.target.value })} data-testid="doctor-voice-input" /></Field>
              <Field label="Specialty"><input className={inputCls} value={doc.specialty} onChange={(e) => setDoc({ ...doc, specialty: e.target.value })} data-testid="doctor-specialty-input" /></Field>
              <Button data-testid="doctor-save-btn">Save doctor</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
